import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAutoReplySettings, updateAutoReplySettings, selectAutoReply } from "../../store/slices/aiSlice";
import { Save, Loader2, MessageSquare } from "lucide-react";
import toast from "react-hot-toast";
import AIPanel, { AI_BTN, AI_INPUT, AI_LABEL } from "./AIPanel";

export default function AutoReplySettings() {
  const dispatch = useDispatch();
  const { data, loading } = useSelector(selectAutoReply);

  const [form, setForm] = useState({
    enabled: false,
    alwaysOn: false,
    offlineHours: { start: "22:00", end: "08:00" },
    replyTemplate: "",
    replyDelay: 5,
    maxAutoRepliesPerLead: 1,
  });

  useEffect(() => {
    dispatch(getAutoReplySettings());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      setForm({
        enabled: data.enabled ?? false,
        alwaysOn: data.alwaysOn ?? false,
        offlineHours: data.offlineHours || { start: "22:00", end: "08:00" },
        replyTemplate: data.replyTemplate || "",
        replyDelay: data.replyDelay || 5,
        maxAutoRepliesPerLead: data.maxAutoRepliesPerLead || 1,
      });
    }
  }, [data]);

  const handleSave = async () => {
    try {
      await dispatch(updateAutoReplySettings(form)).unwrap();
      toast.success("Auto-reply settings saved");
    } catch (err) {
      toast.error(err || "Failed to save");
    }
  };

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const Switch = ({ checked, onChange, label }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={`relative w-11 h-6 flex-shrink-0 rounded-full transition-colors duration-200 ${checked ? "bg-gradient-to-r from-purple-600 to-pink-600" : "bg-gray-200"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${checked ? "translate-x-5" : ""}`} />
    </button>
  );

  const ROW = "flex items-center justify-between gap-4 rounded-xl border border-gray-200/70 bg-gray-50/60 p-4";

  return (
    <AIPanel
      icon={MessageSquare}
      title="Auto-Reply Settings"
      description="Reply to new booking requests automatically when you can't."
    >
      <div className="space-y-4">
        {/* Enable toggle */}
        <div className={ROW}>
          <div>
            <p className="text-sm font-semibold text-gray-900">Enable Auto-Reply</p>
            <p className="text-xs text-gray-500 mt-0.5">Automatically respond to new booking requests</p>
          </div>
          <Switch checked={form.enabled} onChange={() => update("enabled", !form.enabled)} label="Enable auto-reply" />
        </div>

        {form.enabled && (
          <>
            {/* Always on toggle */}
            <div className={ROW}>
              <div>
                <p className="text-sm font-semibold text-gray-900">Always On</p>
                <p className="text-xs text-gray-500 mt-0.5">Reply 24/7, not just offline hours</p>
              </div>
              <Switch checked={form.alwaysOn} onChange={() => update("alwaysOn", !form.alwaysOn)} label="Always on" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Offline hours */}
              {!form.alwaysOn && (
                <div>
                  <p className={`mb-1.5 ${AI_LABEL}`}>Offline Hours</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      aria-label="Offline from"
                      value={form.offlineHours.start}
                      onChange={(e) => update("offlineHours", { ...form.offlineHours, start: e.target.value })}
                      className={AI_INPUT}
                    />
                    <span className="text-xs font-medium text-gray-400">to</span>
                    <input
                      type="time"
                      aria-label="Offline to"
                      value={form.offlineHours.end}
                      onChange={(e) => update("offlineHours", { ...form.offlineHours, end: e.target.value })}
                      className={AI_INPUT}
                    />
                  </div>
                </div>
              )}

              {/* Reply delay */}
              <div>
                <p className={`mb-1.5 ${AI_LABEL}`}>Reply Delay (minutes)</p>
                <input
                  type="number"
                  min={1}
                  max={60}
                  aria-label="Reply delay in minutes"
                  value={form.replyDelay}
                  onChange={(e) => update("replyDelay", parseInt(e.target.value) || 5)}
                  className={AI_INPUT}
                />
              </div>
            </div>

            {/* Template */}
            <div>
              <p className={`mb-1.5 ${AI_LABEL}`}>Reply Template</p>
              <textarea
                value={form.replyTemplate}
                onChange={(e) => update("replyTemplate", e.target.value)}
                rows={4}
                className="w-full px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all resize-none"
                placeholder="Thank you for your interest! We will get back to you shortly..."
              />
            </div>

            {/* Preview — shown as the chat bubble the customer receives */}
            <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-4">
              <p className={`flex items-center gap-1.5 mb-3 ${AI_LABEL}`}>
                <MessageSquare size={12} /> Auto-Reply Preview
              </p>
              <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-white border border-gray-200/70 px-4 py-3 shadow-sm">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                  {form.replyTemplate || "No template set"}
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-2.5">
                Sent after {form.replyDelay} min &middot; {form.alwaysOn ? "24/7" : `${form.offlineHours.start}–${form.offlineHours.end}`}
              </p>
            </div>
          </>
        )}

        <button onClick={handleSave} disabled={loading} className={`${AI_BTN} w-full`}>
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save Settings
        </button>
      </div>
    </AIPanel>
  );
}
